/**
 * There is no server-rendered PDF path (see CLAUDE.md) — a proposal is turned
 * into a PDF client-side, from the exact DOM the preview shows, so the download
 * and the on-screen document can never drift apart.
 *
 * `html2canvas-pro`, not plain `html2canvas`: the app's palette is defined in
 * `oklch()` (styles.css), which classic html2canvas's color parser rejects
 * outright and aborts the whole capture on. The `-pro` fork understands
 * modern CSS color functions.
 *
 * Where the pages fall is not decided here. `./paged.ts` has already laid the
 * document out on sheets — inserting the whitespace that pushes a block off the
 * bottom of one page and onto the top of the next — and hands over the top of
 * each page. This slices the rasterized column at exactly those offsets, which
 * is what makes the printed copy the one the operator was looking at. The
 * gutters between sheets fall between two slices and are never drawn.
 */
import { CONTENT_HEIGHT_PX, PAGE_MARGIN_IN } from "./paged";

/**
 * html2canvas paints an `<img>` that hasn't decoded yet as nothing at all, and
 * reports no error doing it. A letterhead logo is the first thing on the page,
 * so a race here costs the whole point of branding the document; waiting is
 * cheap and never rejects — a logo that won't decode should still yield a
 * proposal, just without it.
 */
async function decodeImages(root: HTMLElement) {
  await Promise.all(
    Array.from(root.querySelectorAll("img")).map((img) => img.decode().catch(() => undefined)),
  );
}

export async function downloadProposalPdf(
  element: HTMLElement,
  filename: string,
  /**
   * Re-measured here rather than passed as a value: it is called once the
   * fonts and the logo have settled, and a page top taken before the
   * letterhead had its real height would cut every page in the wrong place.
   */
  measurePages: () => number[],
) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas-pro"),
    import("jspdf"),
  ]);

  await decodeImages(element);
  // Belt and braces alongside the caller's own font gate: a face still loading
  // rasterizes in the fallback typeface, silently, and the resulting PDF looks
  // finished. `document.fonts.ready` never rejects.
  await document.fonts.ready.catch(() => undefined);

  const pageTops = measurePages();

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
  });

  // Canvas pixels per CSS pixel, measured rather than assumed to equal the
  // requested scale — keeps the slicing aligned with the page tops even if
  // html2canvas's actual output resolution doesn't match what was asked for.
  const canvasPxPerCssPx = canvas.width / (element.getBoundingClientRect().width || 1);

  const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
  const contentWidthIn = pdf.internal.pageSize.getWidth() - PAGE_MARGIN_IN * 2;
  const canvasPxPerIn = canvas.width / contentWidthIn;

  let firstPage = true;
  for (const pageTop of pageTops) {
    const startPx = Math.round(pageTop * canvasPxPerCssPx);
    const endPx = Math.min(
      canvas.height,
      Math.round((pageTop + CONTENT_HEIGHT_PX) * canvasPxPerCssPx),
    );
    const sliceHeightPx = endPx - startPx;
    // A trailing page top can land past the last pixel of content when the
    // document ends exactly on a page edge; there is nothing to print on it.
    if (sliceHeightPx <= 0) continue;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeightPx;
    const ctx = pageCanvas.getContext("2d");
    if (!ctx) throw new Error("Couldn't get a 2D canvas context.");
    // The slice may be shorter than a full page; the rest of the sheet is the
    // white the page was drawn on, so paint it rather than leaving it clear.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      startPx,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx,
    );

    if (!firstPage) pdf.addPage();
    firstPage = false;
    pdf.addImage(
      pageCanvas.toDataURL("image/jpeg", 0.95),
      "JPEG",
      PAGE_MARGIN_IN,
      PAGE_MARGIN_IN,
      contentWidthIn,
      sliceHeightPx / canvasPxPerIn,
    );
  }

  pdf.save(filename);
}
